import { laborManagementApi } from '../laborManagementApi';
import type {
  ProjectionsResponse,
  WeeklyProjectionView,
  UpsertWeeklyProjectionRequest,
} from '../types/laborManagementTypes';

export interface ProjectionQuery {
  company: string;
  centerId: string;
  isoYear?: number;
  isoWeek?: number;
}

// A rejected save must not invalidate the week: the refetch would overwrite every cell the
// manager typed. Lives outside the endpoint builder to keep that function's complexity flat.
export const upsertProjectionTags = (
  error: unknown,
  { company, centerId, isoYear, isoWeek }: UpsertWeeklyProjectionRequest,
) => (error ? [] : [{ type: 'StaffingRule' as const, id: `proj-${company}-${centerId}-${isoYear}-${isoWeek}` }]);

const projectionEndpointsInjected = laborManagementApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /projections?company=&centerId=&isoYear=&isoWeek=
    getProjections: builder.query<ProjectionsResponse, ProjectionQuery>({
      query: ({ company, centerId, isoYear, isoWeek }) => ({
        url: 'projections',
        params: {
          company,
          centerId,
          ...(isoYear != null && isoWeek != null ? { isoYear, isoWeek } : {}),
        },
      }),
      providesTags: (_result, _error, { company, centerId, isoYear, isoWeek }) => [
        { type: 'StaffingRule', id: `proj-${company}-${centerId}-${isoYear ?? 'cur'}-${isoWeek ?? 'cur'}` },
      ],
      keepUnusedDataFor: 60,
    }),

    // POST /projection
    upsertProjection: builder.mutation<WeeklyProjectionView, UpsertWeeklyProjectionRequest>({
      query: (body) => ({
        url: 'projection',
        method: 'POST',
        body,
      }),
      async onQueryStarted(body, { dispatch, queryFulfilled }) {
        const cacheKey: ProjectionQuery = {
          company: body.company,
          centerId: body.centerId,
          isoYear: body.isoYear,
          isoWeek: body.isoWeek,
        };

        const dayCellKey = (dayOfWeek: number, departmentName: string) =>
          `${dayOfWeek}|${departmentName.toLowerCase()}`;

        const dayActualsMap = new Map(
          body.departmentDayActuals.map((a) => [dayCellKey(a.dayOfWeek, a.departmentName), a.actualStaff ?? 0]),
        );

        const patchResult = dispatch(
          projectionEndpointsInjected.util.updateQueryData('getProjections', cacheKey, (draft) => {
            if (!draft.week) return;
            draft.week.effectiveCases = draft.week.scheduledCases;
            draft.week.departmentDayActuals = body.departmentDayActuals.map((a) => ({
              departmentName: a.departmentName,
              dayOfWeek: a.dayOfWeek,
              actualStaff: a.actualStaff ?? 0,
            }));
            draft.week.staffingComment = body.staffingComment;

            const departmentActualTotals = new Map<string, number>();
            for (const [key, actualStaff] of dayActualsMap) {
              const [, departmentName] = key.split('|');
              departmentActualTotals.set(
                departmentName,
                (departmentActualTotals.get(departmentName) ?? 0) + actualStaff,
              );
            }

            if (draft.week.dailyStaffing) {
              for (const day of draft.week.dailyStaffing) {
                for (const department of day.departments) {
                  const key = dayCellKey(day.dayOfWeek, department.departmentName);
                  department.actualStaff = dayActualsMap.has(key) ? dayActualsMap.get(key) ?? 0 : null;
                }
              }
            }

            if (draft.week.staffing) {
              for (const dept of draft.week.staffing.departments) {
                dept.actualStaff = departmentActualTotals.get(dept.departmentName.toLowerCase()) ?? null;
              }
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, error, arg) => upsertProjectionTags(error, arg),
    }),
  }),
});

export const {
  useGetProjectionsQuery,
  useUpsertProjectionMutation,
} = projectionEndpointsInjected;
